// @flow
/* -------------------- Globals ---------------- */
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import uniqBy from 'lodash/uniqBy';
/* -------------------- Types ----------------- */
import type {
  CreateScheduleState,
} from './types';

import {
  SET_GOAL_DATE,
  ADD_TITLE,
  SET_STUDY_DURATION,
  SET_TIME_REMINDER,
  SET_LONG_TERM,
  SET_SCHEDULE_ID,
  DISCARD,
  ADD_LEARNING_CONTENT,
  DISCARD_LEARNING_CONTENT,
  REMOVE_LEARNING_CONTENT,
  SHOW,
  SHOW_DATE_PICKER,
  SET_ARCHIEVE,
  SHOW_EDIT_DATE,
  SELECT_LEARNING_TYPE,
  SET_EDIT_CONTENT,
  SET_EDIT_MODE,
  SHOW_SLIDER,
  SHOW_TIME_PICKER,
  SHOW_EDIT_TIME,
  SHOW_DISCARD_SCHEDULE,
  SET_SELECTED_SCHEDULE_ID,
  SHOW_CONTENT_DIALOG,
  SET_MAX_STUDY_DURATION,
  SET_SEARCH_QUERY,
  SET_ADDED_CONTENTS,
} from './actionTypes';

/* -------------------- Constants ---------------- */
import {
  initialState,
} from './constants';

/* -------------------Utils----------------------- */
import { CustomIntl } from '../utils';

const handleSetAddedContents = (state: CreateScheduleState,
  data: *) => produce(state, draft => {
  draft.addedContents = data;
});
const setGoalDate = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    goalDate,
    date,
  } = data;
  draft.goalDate = goalDate;
  draft.date = date;
});

const addTitle = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    title,
  } = data;
  draft.title = title;
});

const addContent = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    contentItem,
  } = data;
  const { selectedContents } = state;
  const newContentList = [...selectedContents, contentItem];
  const uniqItems = uniqBy(newContentList, 'content.id');
  draft.selectedContents = uniqItems;
});

const discardContent = (
  state: CreateScheduleState,
) => produce(state, (draft) => {
  draft.selectedContents = [];
});

const removeContent = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    id,
  } = data;
  const {
    selectedContents,
  } = state;
  const filteredContents = selectedContents.filter(({
    content: {
      id: contentId,
    },
  }) => contentId !== id);
  draft.selectedContents = filteredContents;
});

const setStudyDuration = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    duration,
  } = data;
  draft.studyDuration = duration;
});

const setReminder = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    time,
    timeValue,
  } = data;
  const reminder = {
    hour: time.hour,
    minutes: time.minutes,
    isAm: time.isAm,
    timeZone: CustomIntl.timezone,
  };
  const {
    reminderTime,
  } = state;
  if (!isEqual(reminderTime, reminder)) {
    draft.reminderTime = reminder;
  }
  draft.time = timeValue;
});

const setLongTerm = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    isLongTerm,
  } = data;
  draft.isLongTerm = isLongTerm;
});

const discardStudySchedule = (state: CreateScheduleState) => produce(
  state,
  draft => {
    draft.id = null;
    draft.title = null;
    draft.selectedContents = [];
    draft.goalDate = null;
    draft.isLongTerm = false;
    draft.studyDuration = 30;
    draft.addedContents = [];
    draft.reminderTime = {
      hour: moment().hour(),
      minutes: 0,
      isAm: moment().format('A') === 'AM',
      timeZone: CustomIntl.timezone,
    };
    draft.editMode = false;
    draft.date = null;
    draft.time = moment().format('hh:00 A');
    draft.maxStudyDuration = 0;
  },
);
const setDatePickerVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    showDatePicker,
  } = data;
  draft.showDatePicker = showDatePicker;
});

const setEditDateVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    showEditDate,
  } = data;
  draft.showEditDate = showEditDate;
});

const setContentEdit = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    isEditContent,
  } = data;
  draft.editContent = isEditContent;
});

const setSelectLearningTypeVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    showLearningModal,
  } = data;
  draft.showLearningModal = showLearningModal;
});

const setDiscardModalVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    showDiscardModal,
  } = data;
  draft.showDiscardModal = showDiscardModal;
});

const setDialogOpen = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    showDialog,
  } = data;
  draft.showContentDialog = showDialog;
});

const getStudySchedule = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const {
    id,
    name,
    goalDate,
    studyDuration,
    isLongTerm,
    contents: {
      folder: {
        contents: {
          edges,
        },
      },
    },
    reminder,
  } = data;

  const mappedEdges = edges.map(({
    node: {
      content,
      metadata,
    },
  }) => ({
    content,
    metadata,
  }));
  draft.id = id;
  draft.isLongTerm = isLongTerm;
  draft.selectedContents = mappedEdges;
  draft.reminderTime = reminder;
  draft.title = name;
  draft.goalDate = goalDate;
  draft.studyDuration = studyDuration;
  draft.editMode = true;
});

const setEditMode = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { isEditMode } = data;
  draft.editMode = isEditMode;
});

const setSliderModalVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { showSlider } = data;
  draft.showSlider = showSlider;
});

const setTimePickerVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { showTimePicker } = data;
  draft.showTimePicker = showTimePicker;
});

const setEditTimeVisible = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { showEditTime } = data;
  draft.showEditTime = showEditTime;
});

const setSelectedScheduleId = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { id } = data;
  draft.detailScheduleId = id;
});
const handleSetArchive = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  draft.isArchived = data;
});

const setMaxStudyDuration = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { maxStudyDuration } = data;
  draft.maxStudyDuration = maxStudyDuration;
});

const setSearchQuery = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  draft.query = data;
});

const setScheduleId = (
  state: CreateScheduleState,
  data: *,
) => produce(state, (draft) => {
  const { id } = data;
  draft.id = id;
});

const createScheduleReducer = (
  state: CreateScheduleState = { ...initialState },
  action: *,
) => {
  switch (action.type) {
    case ADD_TITLE:
      return addTitle(state, action.data);
    case ADD_LEARNING_CONTENT:
      return addContent(state, action.data);
    case DISCARD_LEARNING_CONTENT:
      return discardContent(state);
    case REMOVE_LEARNING_CONTENT:
      return removeContent(state, action.data);
    case SET_GOAL_DATE:
      return setGoalDate(
        state,
        action.data,
      );
    case SET_STUDY_DURATION:
      return setStudyDuration(state, action.data);
    case SET_TIME_REMINDER:
      return setReminder(state, action.data);
    case SET_LONG_TERM:
      return setLongTerm(state, action.data);
    case DISCARD:
      return discardStudySchedule(state);
    case SHOW:
      return getStudySchedule(
        state,
        action.data,
      );
    case SHOW_DATE_PICKER:
      return setDatePickerVisible(
        state,
        action.data,
      );
    case SHOW_EDIT_DATE:
      return setEditDateVisible(
        state,
        action.data,
      );
    case SHOW_TIME_PICKER:
      return setTimePickerVisible(
        state,
        action.data,
      );
    case SHOW_EDIT_TIME:
      return setEditTimeVisible(
        state,
        action.data,
      );
    case SET_EDIT_CONTENT:
      return setContentEdit(
        state,
        action.data,
      );
    case SET_EDIT_MODE:
      return setEditMode(
        state,
        action.data,
      );
    case SHOW_SLIDER:
      return setSliderModalVisible(
        state,
        action.data,
      );
    case SELECT_LEARNING_TYPE:
      return setSelectLearningTypeVisible(
        state,
        action.data,
      );
    case SHOW_DISCARD_SCHEDULE:
      return setDiscardModalVisible(
        state,
        action.data,
      );
    case SET_SELECTED_SCHEDULE_ID:
      return setSelectedScheduleId(
        state,
        action.data,
      );
    case SHOW_CONTENT_DIALOG:
      return setDialogOpen(
        state,
        action.data,
      );
    case SET_MAX_STUDY_DURATION:
      return setMaxStudyDuration(
        state,
        action.data,
      );
    case SET_SEARCH_QUERY:
      return setSearchQuery(
        state,
        action.data,
      );
    case SET_SCHEDULE_ID:
      return setScheduleId(
        state,
        action.data,
      );
    case SET_ARCHIEVE:
      return handleSetArchive(
        state,
        action.data,
      );
    case SET_ADDED_CONTENTS:
      return handleSetAddedContents(state, action.data);
    default:
      return state;
  }
};

export default createScheduleReducer;
